import { Controller } from '@hotwired/stimulus';

// Identifier rendered by Application::Toolkit::AgGridSsrmComponent#data_controller — present in
// this component's DOM only when VoterList::ValidationGridComponent is rendered (`voter-list-
// validation-grid` Flipper flag ON). Flag-off leaves the legacy form-submit path below byte-for-
// byte unchanged.
const SSRM_GRID_CONTROLLER_IDENTIFIER = 'application--toolkit--ag-grid-ssrm-component--ag-grid-ssrm-component';

export default class extends Controller {
  static targets = ['form'];

  // Gap A bridge (86e277pna importation-overview follow-up) — when the SSRM validation grid is
  // present, route the filter/keyword UI into it directly instead of firing the legacy `<form>`
  // (which the grid never listens to). Flag-off (grid absent) is untouched: same
  // `Rails.fire(this.formTarget, 'submit')` as before.
  submit() {
    const gridElement = this.gridElement();

    if (gridElement) {
      this.submitToGrid(gridElement);
      return;
    }

    Rails.fire(this.formTarget, 'submit');
  }

  // Browser-QA hardening (importation-overview follow-up) — `change->overview#submit` above is
  // the PRIMARY driver, but it is not the ONLY path that can ever submit this form (the legacy
  // page-wide jQuery auto-submit binding — `EB.Elections.VoterListController#init`/
  // `#initEditorGridSettingsAutoSubmit`, `elections.voter_list.coffee` — still runs in both modes
  // and can fire `Rails.fire(form, 'submit')` on its own; so can a plain Enter keypress in the
  // keyword field). Whatever the source, a real 'submit' event always reaches the FORM element
  // itself before Rails-UJS's own document-level delegated handler ever sees it (event order:
  // target/bubble-phase listeners on the form run before the event bubbles to document) — so
  // intercepting it HERE, on the form, converges every submission path onto the SAME single
  // bridge call whenever the grid is present. `stopImmediatePropagation` additionally guarantees
  // Rails-UJS's own submit handling never runs at all for this event, not just that its default
  // is prevented — the same "stop the OTHER handler from ever seeing this event" requirement that a
  // capture-phase listener solves, resolved here with `submit` on the form's own target phase,
  // which wins for the same reason: the event fires ON the form before it bubbles to document.
  interceptSubmit(event) {
    const gridElement = this.gridElement();
    if (!gridElement) return; // flag-off: legacy submission proceeds exactly as before

    event.preventDefault();
    event.stopImmediatePropagation();
    this.submitToGrid(gridElement);
  }

  gridElement() {
    return this.element.querySelector(`[data-controller~="${SSRM_GRID_CONTROLLER_IDENTIFIER}"]`);
  }

  submitToGrid(gridElement) {
    const gridController = this.application.getControllerForElementAndIdentifier(
      gridElement,
      SSRM_GRID_CONTROLLER_IDENTIFIER
    );
    if (!gridController) return;

    const formData = new FormData(this.formTarget);

    // Keyword is routed through the grid's OWN existing searchText mechanism (not a new
    // transport) — the grid's own quick-filter input (if present) and this keyword both write the
    // same `searchTextValue`; last writer wins, same as any other Stimulus Value.
    gridController.searchTextValue = (formData.get('keyword') || '').toString();

    gridController.setExternalFilters({
      notice_types: formData.getAll('filters[notice_types][]'),
      groups: formData.getAll('filters[groups][]'),
      errors: formData.getAll('filters[errors][]')
    });
  }
}
